import { computed, signal, Signal } from "@angular/core";
import { TileItem } from "./item.tile.component";

/**
 * Interface for paginated data that can be displayed in search results
 */
export interface IPaginatedData<T = any> {
    readonly items: Signal<T[]>;
    readonly itemsForDisplay: Signal<TileItem[]>;
    readonly page: Signal<number>;
    readonly loading: Signal<boolean>;
    readonly loadingAll: Signal<boolean>;
    readonly totalCount: Signal<number>;
    readonly hasMore: Signal<boolean>;
    loadMore(): Promise<PaginatedLoadResult>;
    loadAll(): Promise<PaginatedLoadResult>;
}

export interface PaginatedLoadResult {
    loaded: number;
    error?: unknown;
}

/**
 * Response shape from paginated API endpoints
 */
export interface PaginatedResponse<T = any> {
    items?: T[];
    events?: T[]; // For backward compatibility with event endpoints
    organizations?: T[]; // For backward compatibility with organization endpoints
    paginate_key?: string;
    page_num: number;
    page_count: number;
    total_results?: number;
}

/**
 * Generic paginated list that works with any data type.
 * Manages pagination state, data fetching, and conversion to display format.
 */
export class PaginatedList<T = any> implements IPaginatedData<T> {
    private fetchFn?: (page: number, pageSize: number, key: string) => Promise<PaginatedResponse<T>>;
    private convertFn: (item: T) => TileItem;
    private converterFn: (items: any[]) => T[];
    private pageSize: number = 10;

    items = signal<T[]>([]);
    page = signal(1);
    paginateKey = signal("T");
    hasMore = signal(false);
    loading = signal(false);
    loadingAll = signal(false);
    totalCount = signal(0);

    itemsForDisplay: Signal<TileItem[]> = computed(() => this.items().map((item) => this.convertFn(item)));

    /**
     * @param convertToTileItem Function to convert a single item to TileItem for display
     * @param convertApiResponse Function to convert API response items to domain model (optional, defaults to identity)
     */
    constructor(convertToTileItem: (item: T) => TileItem, convertApiResponse?: (items: any[]) => T[]) {
        this.convertFn = convertToTileItem;
        this.converterFn = convertApiResponse ?? ((items: any[]) => items as T[]);
    }

    /**
     * Initialize the list with data from the first page
     */
    setInitialData(response: PaginatedResponse<T>) {
        const rawItems = response.items ?? [];
        this.items.set(this.converterFn(rawItems));
        this.page.set(1);
        this.paginateKey.set(response.paginate_key || "T");
        this.hasMore.set(response.page_num < response.page_count);
        this.totalCount.set(response.total_results || rawItems.length);
    }

    /**
     * Configure the fetch function for loading more data
     */
    configureFetch(
        fetchFn: (page: number, pageSize: number, key: string) => Promise<PaginatedResponse<T>>,
        pageSize: number = 10,
    ) {
        this.fetchFn = fetchFn;
        this.pageSize = pageSize;
    }

    /**
     * Load more items and append to the existing list
     */
    async loadMore(): Promise<PaginatedLoadResult> {
        if (!this.fetchFn || this.loading() || !this.hasMore()) return { loaded: 0 };

        try {
            this.loading.set(true);
            const nextPage = this.page() + 1;
            const response = await this.fetchFn(nextPage, this.pageSize, this.paginateKey());

            const newItems = this.converterFn(response.items ?? []);
            // Append new items to existing ones
            this.items.update((prevItems) => [...prevItems, ...newItems]);

            this.page.set(nextPage);
            this.paginateKey.set(response.paginate_key || "T");
            this.hasMore.set(response.page_num < response.page_count);
            return { loaded: newItems.length };
        } catch (error) {
            console.error("Error loading more items:", error);
            return { loaded: 0, error };
        } finally {
            this.loading.set(false);
        }
    }

    /**
     * Load all remaining items at once. If a page fails partway, the pages already fetched are kept.
     */
    async loadAll(): Promise<PaginatedLoadResult> {
        if (!this.fetchFn || this.loading() || this.loadingAll() || !this.hasMore()) return { loaded: 0 };

        const startCount = this.items().length;
        let currentPage = this.page();
        let currentKey = this.paginateKey();
        let allItems = [...this.items()];

        try {
            this.loadingAll.set(true);
            let keepLoading = true;

            while (keepLoading) {
                const nextPage = currentPage + 1;
                const response = await this.fetchFn(nextPage, this.pageSize, currentKey);

                const rawItems = response.items ?? [];
                allItems = [...allItems, ...this.converterFn(rawItems)];

                currentPage = nextPage;
                currentKey = response.paginate_key || "T";
                keepLoading = response.page_num < response.page_count && rawItems.length > 0;

                // give api time to finish caching before next request
                if (keepLoading) {
                    await new Promise((resolve) => setTimeout(resolve, 500));
                }
            }

            this.items.set(allItems);
            this.page.set(currentPage);
            this.paginateKey.set(currentKey);
            this.hasMore.set(false);
            return { loaded: allItems.length - startCount };
        } catch (error) {
            console.error("Error loading all items:", error);
            this.items.set(allItems);
            this.page.set(currentPage);
            this.paginateKey.set(currentKey);
            return { loaded: allItems.length - startCount, error };
        } finally {
            this.loadingAll.set(false);
        }
    }

    /**
     * Reset the list to empty state
     */
    reset() {
        this.items.set([]);
        this.page.set(1);
        this.paginateKey.set("T");
        this.hasMore.set(false);
        this.totalCount.set(0);
    }
}
